import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextField } from '../../components/Field';
import { useStore } from '../../store/StoreProvider';
import { PopTownLogo } from '../../components/Brand';
import { Icon } from '../../components/Icon';
import { TownScene } from './TownScene';
import { MotionToggle, useTownMotion } from './townMotion';
import { loginWithPasskey, passkeysSupported } from '../../lib/passkeys';

const MIN_PASSWORD = 8;

const ERRORS = {
  creds: 'Credenziali non valide.',
  short: `La password deve avere almeno ${MIN_PASSWORD} caratteri.`,
  throttled: 'Troppi tentativi: riprova fra qualche minuto.',
} as const;

// Il login è SEMPRE richiesto, anche da rete locale (§6.0). Il primo accesso crea
// l'amministratore con utente e password scelti in quel momento. Dopo si entra anche con una
// passkey (0.1.28): il browser chiede quale, senza nome utente.
export function Login() {
  const { dispatch, state } = useStore();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [busyPw, setBusyPw] = useState(false);
  const [error, setError] = useState<'creds' | 'short' | 'throttled' | null>(null);
  const [needsOwner, setNeedsOwner] = useState(false);
  useEffect(() => {
    api
      .sessionInfo()
      .then((info) => setNeedsOwner(!!info.needsOwner))
      .catch(() => undefined);
  }, []);
  const [shake, setShake] = useState(false);
  const [motion, setMotion] = useTownMotion();
  const [showPw, setShowPw] = useState(false);

  const fail = (kind: 'creds' | 'short' | 'throttled') => {
    setError(kind);
    setShake(true);
    setPassword('');
    window.setTimeout(() => setShake(false), 200);
  };

  const withPassword = async () => {
    if (!password) {
      fail('creds');
      return;
    }
    if (needsOwner && password.length < MIN_PASSWORD) {
      fail('short');
      return;
    }
    setBusyPw(true);
    setError(null);
    try {
      await api.login({ user, password });
      dispatch({ type: 'auth/info', info: await api.sessionInfo() });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      fail(status === 429 ? 'throttled' : status === 400 ? 'short' : 'creds');
    } finally {
      setBusyPw(false);
    }
  };

  const [busyKey, setBusyKey] = useState(false);
  const withPasskey = async () => {
    setBusyKey(true);
    setError(null);
    try {
      await loginWithPasskey();
      dispatch({ type: 'auth/info', info: await api.sessionInfo() });
    } catch (e) {
      // chiudere la finestra del sistema non è un errore
      if (e instanceof DOMException && e.name === 'NotAllowedError') return;
      fail(e instanceof ApiError && e.status === 429 ? 'throttled' : 'creds');
    } finally {
      setBusyKey(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void withPassword();
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh / var(--zoom))',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <TownScene motion={motion} />
      <MotionToggle motion={motion} onChange={setMotion} />
      <form
        className="login-card"
        onSubmit={submit}
        style={{
          position: 'relative',
          // sul telefono la card non esce dallo schermo (padding della pagina: 24 per lato)
          width: 'min(380px, calc(100vw - 48px))',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-2xl)',
          boxShadow: 'var(--shadow-2)',
          padding: '36px 32px 32px',
          marginTop: -40,
          transform: shake ? 'translateX(6px)' : undefined,
          transition: 'transform var(--dur-2)',
        }}
      >
        <div style={{ display: 'grid', placeItems: 'center', marginBottom: 28 }}>
          <PopTownLogo height={110} />
        </div>

        {needsOwner && (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="info">
              Primo accesso: scegli nome utente e password dell&apos;amministratore (almeno{' '}
              {MIN_PASSWORD} caratteri).
            </Banner>
          </div>
        )}
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Banner kind="danger">{ERRORS[error]}</Banner>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 22 }}>
          <Field label="Nome utente">
            <TextField value={user} onChange={setUser} autoComplete="username" autoFocus />
          </Field>
          <Field label="Password">
            <TextField
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              autoComplete={needsOwner ? 'new-password' : 'current-password'}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Nascondi la password' : 'Mostra la password'}
                  style={{
                    width: 32,
                    height: 32,
                    border: 'none',
                    background: 'none',
                    color: 'var(--dim)',
                    display: 'grid',
                    placeItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Icon name={showPw ? 'eye-off' : 'eye'} size={18} />
                </button>
              }
            />
          </Field>
        </div>

        <Button type="submit" variant="primary" size="lg" full loading={busyPw}>
          {needsOwner ? 'Crea ed entra' : 'Accedi'}
        </Button>
        {!needsOwner && passkeysSupported() && (
          <div style={{ marginTop: 10 }}>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              full
              icon="fingerprint"
              loading={busyKey}
              onClick={() => void withPasskey()}
            >
              Entra con una passkey
            </Button>
          </div>
        )}
        {/* la versione solo quando l'API l'ha detta: senza, restava una «v» da sola */}
        {state.version && (
          <div className="t-mono-xs faint" style={{ textAlign: 'center', marginTop: 22 }}>
            v{state.version}
          </div>
        )}
      </form>
    </div>
  );
}
